<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';

/**
 * LaisActionReasonModal — confirmation modal for impactful, hard-to-reverse
 * actions (replay, refusal, blacklist, erasure, cancelamento, cobrança,
 * publish externo). It forces the operator to acknowledge the consequence and
 * (optionally) type a reason before the destructive action can run.
 *
 * It is purely presentational: the domain passes normalized copy + a
 * consequence string and listens for a single `confirm` event carrying the
 * captured `{ reason, acknowledged }`. No business rules live here.
 */
const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  // Explains the impact of the action; rendered prominently in a warning tint.
  consequence: { type: String, default: '' },
  reasonLabel: { type: String, default: '' },
  reasonRequired: { type: Boolean, default: true },
  reasonPlaceholder: { type: String, default: '' },
  // When set, renders a required acknowledgement checkbox with this label.
  acknowledgeLabel: { type: String, default: '' },
  confirmationLabel: { type: String, default: '' },
  confirmationPhrase: { type: String, default: '' },
  confirmationPlaceholder: { type: String, default: '' },
  confirmText: { type: String, default: '' },
  cancelText: { type: String, default: '' },
  confirmVariant: {
    type: String,
    default: 'danger',
    validator: value => ['primary', 'danger'].includes(value),
  },
  isLoading: { type: Boolean, default: false },
  maxReasonLength: { type: Number, default: 500 },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-action-reason-${instance?.uid || 'default'}`;
const reasonId = `${baseId}-reason`;
const reasonHintId = `${baseId}-reason-hint`;
const counterId = `${baseId}-counter`;
const consequenceId = `${baseId}-consequence`;
const acknowledgeId = `${baseId}-acknowledge`;
const confirmationId = `${baseId}-confirmation`;
const confirmationHintId = `${baseId}-confirmation-hint`;

const reason = ref('');
const acknowledged = ref(false);
const confirmation = ref('');

// Reset the captured reason/ack every time the dialog is (re)opened so a stale
// value from a previous action never leaks into the next confirmation.
watch(
  () => props.show,
  isOpen => {
    if (isOpen) {
      reason.value = '';
      acknowledged.value = false;
      confirmation.value = '';
    }
  },
  { immediate: true }
);

const CONFIRM_VARIANT_CLASSES = Object.freeze({
  primary: 'bg-lais-primary text-white hover:bg-lais-primary-600',
  danger: 'bg-lais-danger text-white hover:bg-lais-danger/90',
});

const resolvedReasonLabel = computed(
  () => props.reasonLabel || t('LAIS_ACTION_REASON_MODAL.REASON_LABEL')
);
const resolvedReasonPlaceholder = computed(
  () =>
    props.reasonPlaceholder || t('LAIS_ACTION_REASON_MODAL.REASON_PLACEHOLDER')
);
const resolvedConfirmText = computed(
  () => props.confirmText || t('LAIS_ACTION_REASON_MODAL.CONFIRM')
);
const resolvedCancelText = computed(
  () => props.cancelText || t('LAIS_ACTION_REASON_MODAL.CANCEL')
);
const requiredHint = computed(() =>
  t('LAIS_ACTION_REASON_MODAL.REQUIRED_HINT')
);
const requiredMarker = '*';
const counterLabel = computed(() =>
  t('LAIS_ACTION_REASON_MODAL.COUNTER', {
    current: reason.value.length,
    max: props.maxReasonLength,
  })
);

const trimmedReason = computed(() => reason.value.trim());
const hasAcknowledge = computed(() => Boolean(props.acknowledgeLabel));
const hasTypedConfirmation = computed(() =>
  Boolean(props.confirmationPhrase?.trim())
);
const reasonSatisfied = computed(
  () => !props.reasonRequired || trimmedReason.value.length > 0
);
const acknowledgeSatisfied = computed(
  () => !hasAcknowledge.value || acknowledged.value
);
const confirmationSatisfied = computed(
  () =>
    !hasTypedConfirmation.value ||
    confirmation.value.trim().toUpperCase() ===
      props.confirmationPhrase.trim().toUpperCase()
);
const canConfirm = computed(
  () =>
    reasonSatisfied.value &&
    acknowledgeSatisfied.value &&
    confirmationSatisfied.value &&
    !props.isLoading
);

const confirmButtonClass = computed(
  () =>
    CONFIRM_VARIANT_CLASSES[props.confirmVariant] ||
    CONFIRM_VARIANT_CLASSES.danger
);

const reasonDescribedBy = computed(() => {
  const ids = [];
  if (props.reasonRequired) ids.push(reasonHintId);
  ids.push(counterId);
  return ids.join(' ');
});

const handleClose = () => emit('close');

const handleConfirm = () => {
  if (!canConfirm.value) return;
  emit('confirm', {
    reason: trimmedReason.value,
    acknowledged: acknowledged.value,
    confirmation: confirmation.value.trim(),
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[520px]"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <p
        v-if="description"
        class="text-lais-body-sm leading-relaxed text-lais-text-body"
      >
        {{ description }}
      </p>

      <div
        v-if="consequence"
        :id="consequenceId"
        role="note"
        class="flex items-start gap-2.5 rounded-lais border border-lais-warning/40 bg-lais-warning/10 px-3.5 py-3"
      >
        <span
          class="i-lucide-triangle-alert mt-0.5 size-5 shrink-0 text-lais-warning"
          aria-hidden="true"
        />
        <p class="text-lais-body-sm font-medium leading-relaxed text-lais-text">
          {{ consequence }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <label
          :for="reasonId"
          class="flex items-center gap-1 text-lais-label-sm font-semibold text-lais-text-body"
        >
          <span>{{ resolvedReasonLabel }}</span>
          <span
            v-if="reasonRequired"
            class="text-lais-danger"
            aria-hidden="true"
          >
            {{ requiredMarker }}
          </span>
        </label>
        <textarea
          :id="reasonId"
          v-model="reason"
          rows="4"
          :placeholder="resolvedReasonPlaceholder"
          :maxlength="maxReasonLength"
          :required="reasonRequired"
          :aria-required="reasonRequired ? 'true' : undefined"
          :aria-describedby="reasonDescribedBy"
          class="w-full resize-y rounded-lais border border-lais-border bg-lais-bg-card px-3 py-2 text-lais-body-sm text-lais-text placeholder:text-lais-text-muted focus-visible:outline-none focus-visible:shadow-lais-focus"
        />
        <div class="flex items-start justify-between gap-3">
          <p
            v-if="reasonRequired"
            :id="reasonHintId"
            class="text-lais-body-xs text-lais-text-helper"
          >
            {{ requiredHint }}
          </p>
          <p
            :id="counterId"
            class="ml-auto shrink-0 text-lais-body-xs tabular-nums text-lais-text-muted"
            aria-live="polite"
          >
            {{ counterLabel }}
          </p>
        </div>
      </div>

      <label
        v-if="hasAcknowledge"
        :for="acknowledgeId"
        class="flex cursor-pointer items-start gap-2.5 rounded-lais border border-lais-border-soft bg-lais-bg-soft px-3.5 py-3"
      >
        <input
          :id="acknowledgeId"
          v-model="acknowledged"
          type="checkbox"
          :aria-describedby="consequence ? consequenceId : undefined"
          class="mt-0.5 size-4 shrink-0 rounded-lais-sm border-lais-border text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
        />
        <span class="text-lais-body-sm text-lais-text-body">
          {{ acknowledgeLabel }}
        </span>
      </label>

      <div v-if="hasTypedConfirmation" class="flex flex-col gap-1.5">
        <label
          :for="confirmationId"
          class="text-lais-label-sm font-semibold text-lais-text-body"
        >
          {{
            confirmationLabel ||
            t('LAIS_ACTION_REASON_MODAL.CONFIRMATION_LABEL')
          }}
        </label>
        <input
          :id="confirmationId"
          v-model="confirmation"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :placeholder="
            confirmationPlaceholder ||
            t('LAIS_ACTION_REASON_MODAL.CONFIRMATION_PLACEHOLDER', {
              phrase: confirmationPhrase,
            })
          "
          :aria-describedby="confirmationHintId"
          class="h-10 w-full rounded-lais border border-lais-border bg-lais-bg-card px-3 text-lais-body-sm font-semibold uppercase tracking-normal text-lais-text placeholder:normal-case placeholder:font-normal placeholder:text-lais-text-muted focus-visible:outline-none focus-visible:shadow-lais-focus"
        />
        <p
          :id="confirmationHintId"
          class="text-lais-body-xs text-lais-text-helper"
        >
          {{
            t('LAIS_ACTION_REASON_MODAL.CONFIRMATION_HINT', {
              phrase: confirmationPhrase,
            })
          }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          data-testid="lais-action-reason-cancel"
          class="h-10 rounded-lais px-4 text-lais-body-sm font-semibold text-lais-text-body transition-colors hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="handleClose"
        >
          {{ resolvedCancelText }}
        </button>
        <button
          type="button"
          data-testid="lais-action-reason-confirm"
          class="inline-flex h-10 items-center gap-2 rounded-lais px-4 text-lais-body-sm font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60"
          :class="confirmButtonClass"
          :disabled="!canConfirm"
          :aria-busy="isLoading ? 'true' : undefined"
          @click="handleConfirm"
        >
          <span
            v-if="isLoading"
            class="i-lucide-loader-circle size-4 shrink-0 animate-spin"
            aria-hidden="true"
          />
          <span>{{ resolvedConfirmText }}</span>
        </button>
      </div>
    </template>
  </LaisModal>
</template>
